import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User, LoginCredentials, AuthState } from '../types';
import { authService } from '../services/api/authService';
import logger from '../lib/logger';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'ma_checker_token';
const USER_KEY = 'ma_checker_user';
const REFRESH_TOKEN_KEY = 'ma_checker_refresh_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const setUser = useCallback((user: User | null) => {
    setState({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    });
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const { user, tokens } = await authService.login(credentials);

      // トークンを保存
      if (credentials.rememberMe) {
        localStorage.setItem(TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        if (tokens.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        }
      } else {
        sessionStorage.setItem(TOKEN_KEY, tokens.accessToken);
        sessionStorage.setItem(USER_KEY, JSON.stringify(user));
      }

      setUser(user);
      logger.info('Login successful', { userId: user.id });
    } catch (error) {
      logger.error('Login failed', error);
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      logger.warn('Logout API call failed', error);
    }

    // ローカルストレージをクリア
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);

    setUser(null);
    logger.info('Logout successful');
  }, [setUser]);

  const refreshAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);

    if (token && userJson) {
      try {
        // 保存されたユーザー情報を使用
        const user = JSON.parse(userJson) as User;
        setUser(user);
        logger.debug('Auth restored from localStorage');

        // バックグラウンドでユーザー情報を更新
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          localStorage.setItem(USER_KEY, JSON.stringify(currentUser));
        } else {
          // トークンが無効な場合はログアウト
          await logout();
        }
      } catch {
        await logout();
      }
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [setUser, logout]);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
